import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SEOHead from "@/components/feature/SEOHead";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { mangaList, mangaCategories } from "@/mocks/manga";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const activeSlug = slug || "all";
  const [activeCategory, setActiveCategory] = useState(activeSlug);
  const [sortBy, setSortBy] = useState<"popular" | "latest" | "rating">("popular");

  useEffect(() => {
    setActiveCategory(activeSlug);
  }, [activeSlug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [activeSlug, sortBy]);

  const currentCategory = mangaCategories.find((c) => c.slug === activeCategory) || mangaCategories[0];

  const filteredManga = mangaList.filter((manga) => {
    if (activeCategory === "all" || activeCategory === "hot") return true;
    if (activeCategory === "new") return true;
    if (activeCategory === "completed") return manga.status === "已完结";
    return manga.category === activeCategory;
  });

  const sortedManga = [...filteredManga].sort((a, b) => {
    if (sortBy === "popular") return b.views - a.views;
    if (sortBy === "latest") return new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime();
    return b.rating - a.rating;
  });

  const categoryTitle = currentCategory.name;
  const pageTitle = `${categoryTitle} - 3077甜蜜暴击第二季漫画 - 36漫画网`;
  const pageDesc = `36漫画网${categoryTitle}专区，为您精选优质漫画作品，高清画质在线免费阅读。海量正版漫画，每日更新，精彩不容错过。`;
  const pageKeywords = `3077甜蜜暴击第二季,漫画,${categoryTitle},在线免费阅读,36漫画网`;

  const breadcrumbItems = [
    { label: "首页", path: "/" },
    { label: categoryTitle },
  ];

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={pageKeywords}
        canonicalUrl={`https://www.36manhua.com/category/${activeCategory}`}
      />
      <Navbar />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 py-6 bg-background-50 border-b border-background-200/70">
          <div className="max-w-[1280px] mx-auto">
            <Breadcrumb items={breadcrumbItems} />
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mt-2">
              <i className={`${currentCategory.icon} text-primary-500 mr-3`}></i>
              {categoryTitle}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              共 {sortedManga.length} 部作品 · 每日更新 · 高清画质
            </p>
          </div>
        </div>

        <div className="w-full px-4 md:px-6">
          <div className="max-w-[1280px] mx-auto">
            {/* Category Tabs */}
            <div className="py-4 border-b border-background-200/70 overflow-x-auto">
              <div className="flex items-center gap-1.5 flex-wrap">
                {mangaCategories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                      activeCategory === cat.slug
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-600 hover:text-primary-500 hover:bg-primary-50"
                    }`}
                  >
                    <i className={`${cat.icon} text-sm`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Sort Options */}
            <div className="py-4 flex items-center gap-3 flex-wrap">
              <span className="text-sm text-foreground-500">排序：</span>
              {[
                { key: "popular", label: "按人气" },
                { key: "latest", label: "按更新" },
                { key: "rating", label: "按评分" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key as typeof sortBy)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                    sortBy === opt.key
                      ? "bg-secondary-500 text-background-50"
                      : "text-foreground-600 hover:bg-secondary-100"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Manga Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6 py-6">
              {sortedManga.map((manga) => (
                <Link
                  key={manga.id}
                  to={`/manga/${manga.slug}`}
                  className="group block"
                >
                  <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
                    <img
                      src={manga.coverImage}
                      alt={manga.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                      <span className="text-background-50 text-sm font-medium">
                        {manga.status === "已完结" ? "已完结 · 共" + manga.chapters[0].chapterNumber + "话" : "连载中"}
                      </span>
                    </div>
                    {manga.status === "已完结" && (
                      <span className="absolute top-2 right-2 bg-accent-500 text-background-50 text-xs px-2 py-0.5 rounded-full font-medium">
                        完结
                      </span>
                    )}
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1 mb-1">
                    {manga.title}
                  </h3>
                  <div className="flex items-center gap-2 text-xs text-foreground-500">
                    <span>{manga.author}</span>
                    <span>·</span>
                    <span className="flex items-center gap-0.5">
                      <i className="ri-star-fill text-accent-500 text-xs"></i>
                      {manga.rating}
                    </span>
                  </div>
                </Link>
              ))}
            </div>

            {sortedManga.length === 0 && (
              <div className="py-20 text-center">
                <i className="ri-inbox-line text-5xl text-foreground-300 mb-4 block"></i>
                <p className="text-foreground-500">该分类下暂无作品</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}